// ─── Deep Insights tab ───────────────────────────────────────────────
// Shared between the founder Deep Insights view and the admin data room
// analytics tab. Renders charts, an aggregates panel, the AI engagement
// insights report and an anonymized-style investor list.

import { useState } from 'react'
import {
  type AiReportData,
  AiReportEmptyState,
  KeyFindingsCards,
  ProfilePerception,
  RecommendationsPanel,
} from './ai-report-section.js'

// ─── Types ───────────────────────────────────────────────────────────

export interface DeepInsightsVisitor {
  id: string
  initials: string
  /** Null while the visitor is still below the email gate. */
  email: string | null
  lastVisit: string
  visits: number
  timeSpent: string
  clickCount: number
  engagementLevel?: string
  linkId?: string
  strengthSeconds: number
  riskSeconds: number
}

export interface StrengthRiskRatio {
  strengthPct: number
  riskPct: number
  strengthSeconds: number
  riskSeconds: number
}

export interface SourceTimeBreakdown {
  profile: number
  pitchVideo: number
  pitchDeck: number
  documents: number
  totalSeconds: number
}

export interface DeepInsightsAggregates {
  totalVisitors: number
  returningVisitors: number
  returnRatePct: number
  avgSessionDurationSeconds: number
  avgExpansionsPerVisitor: number
  engagementDistribution: {
    summaryOnly: number
    quickScan: number
    engaged: number
    deepDive: number
  }
  dimensionAverages: { dimension: string; avgDwellSeconds: number }[]
  topReactions: {
    statementId: string
    statementType: 'argument' | 'risk'
    upCount: number
    downCount: number
  }[]
}

export interface DeepInsightsTabShareLink {
  id: string
  name: string
}

export interface DeepInsightsLinkStat {
  id: string
  name: string
  views: number
  visitors: number
  avgTime: string
  /** Optional public URL to the share link. */
  url?: string
}

export interface DeepInsightsAnalyticsFilter {
  excludedDataRoomCount: number
  excludedEmailCount: number
  excludedDomainCount: number
  hasActiveFilters: boolean
}

// ─── Helpers ─────────────────────────────────────────────────────────

const DIMENSION_LABELS: Record<string, string> = {
  team: 'Team',
  traction: 'Traction',
  market: 'Market',
  problem_solution: 'Problem / Solution',
  vision: 'Vision',
}

function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

// ─── Aggregates Panel ─────────────────────────────────────────────────

function AggregatesPanel({ aggregates }: { aggregates: DeepInsightsAggregates }) {
  const [showAllReactions, setShowAllReactions] = useState(false)
  const hasMore = aggregates.topReactions.length > 5
  const visibleReactions = showAllReactions
    ? aggregates.topReactions
    : aggregates.topReactions.slice(0, 5)

  const hasDimensionAverages = aggregates.dimensionAverages.some((d) => d.avgDwellSeconds > 0)
  const hasReactions = aggregates.topReactions.length > 0
  if (!hasDimensionAverages && !hasReactions) return null

  return (
    <div className="space-y-4 rounded-xl border border-border bg-card/30 p-5">
      {hasDimensionAverages && (
        <div>
          <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Avg dwell per dimension
          </p>
          <div className="grid grid-cols-5 gap-3">
            {aggregates.dimensionAverages.map((d) => (
              <div
                key={d.dimension}
                className="rounded-lg border border-border bg-background/40 px-3 py-2"
              >
                <p className="text-[10px] text-muted-foreground">
                  {DIMENSION_LABELS[d.dimension] ?? d.dimension}
                </p>
                <p className="text-sm font-semibold tabular-nums text-foreground">
                  {formatSeconds(d.avgDwellSeconds)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasReactions && (
        <div>
          <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Top investor reactions
          </p>
          <div className="space-y-1.5">
            {visibleReactions.map((r) => (
              <div
                key={r.statementId}
                className="flex items-start justify-between gap-3 rounded-lg border border-border bg-background/40 px-3 py-2"
              >
                <div className="flex-1">
                  <p className="text-xs leading-relaxed text-foreground">{r.statementId}</p>
                  <p className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                    {r.statementType}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2 text-xs">
                  {r.upCount > r.downCount && (
                    <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-emerald-400">
                      ▲ Positive
                    </span>
                  )}
                  {r.downCount > r.upCount && (
                    <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-red-400">
                      ▼ Negative
                    </span>
                  )}
                  {r.upCount === r.downCount && r.upCount > 0 && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
                      Mixed
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          {hasMore && (
            <button
              type="button"
              onClick={() => setShowAllReactions((v) => !v)}
              className="mt-2 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              {showAllReactions
                ? 'Show less ↑'
                : `Show all reactions (${aggregates.topReactions.length}) →`}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Analytics filter banner ────────────────────────────────────────

function AnalyticsFilterBanner({
  filter,
  onManage,
}: {
  filter: DeepInsightsAnalyticsFilter
  onManage?: () => void
}) {
  const parts: string[] = []
  if (filter.excludedEmailCount > 0) {
    parts.push(`${filter.excludedEmailCount} email${filter.excludedEmailCount === 1 ? '' : 's'}`)
  }
  if (filter.excludedDomainCount > 0) {
    parts.push(`${filter.excludedDomainCount} domain${filter.excludedDomainCount === 1 ? '' : 's'}`)
  }
  if (filter.excludedDataRoomCount > 0) {
    parts.push(
      `${filter.excludedDataRoomCount} link${filter.excludedDataRoomCount === 1 ? '' : 's'}`
    )
  }
  const summary = parts.join(', ')

  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-muted/30 px-4 py-3 text-xs">
      <span className="text-muted-foreground">
        Analytics filters active: {summary} excluded from aggregates and AI reports.
      </span>
      {onManage && (
        <button
          type="button"
          onClick={onManage}
          className="font-medium text-foreground underline-offset-2 hover:underline"
        >
          Manage →
        </button>
      )}
    </div>
  )
}

// ─── Main tab component ──────────────────────────────────────────────

export function DeepInsightsTab({
  aggregates,
  aiReport,
  aiReportGeneratedAt: _aiReportGeneratedAt,
  analyticsFilter,
  onManageAnalyticsFilter,
}: {
  aggregates: DeepInsightsAggregates | null
  aiReport: AiReportData | null
  aiReportGeneratedAt: string | null
  analyticsFilter?: DeepInsightsAnalyticsFilter
  onManageAnalyticsFilter?: () => void
}) {
  // Backend returns `aggregates: null` when the visitor pool is below the
  // minimum threshold — mirrors AI report gating.
  const showAggregates = aggregates !== null

  const hasReport = !!aiReport

  return (
    <div className="space-y-6">
      {/* 1. Filter banners */}
      {analyticsFilter?.hasActiveFilters && (
        <AnalyticsFilterBanner filter={analyticsFilter} onManage={onManageAnalyticsFilter} />
      )}

      {/* 2. Key Findings */}
      {hasReport && <KeyFindingsCards findings={aiReport.keyFindings} />}

      {/* 3. Recommended Actions (most actionable — right after findings) */}
      {hasReport && <RecommendationsPanel recommendations={aiReport.detail.recommendations} />}

      {/* 5. Engagement aggregates */}
      {showAggregates && aggregates && <AggregatesPanel aggregates={aggregates} />}

      {/* 6. Profile Perception (merged strengths + risks) */}
      {hasReport && (
        <ProfilePerception
          dimensions={aiReport.detail.strengthsWeaknesses}
          risks={aiReport.detail.riskPerception}
        />
      )}

      {/* Empty state when no AI report yet */}
      {!hasReport && showAggregates && <AiReportEmptyState />}
    </div>
  )
}
